import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import type { inferRouterInputs } from '@trpc/server';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingStrategies } from '@/hooks/useTradingStrategies';
import { useEntityLists } from '@/hooks/useEntityLists';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import { DEFAULT_REQUISITIES_STALE_TIME, OPTIONS_DATA_START_DATETIME } from '@/config/constants';
import {
    Button,
    FormDateInput,
    FormInput,
    FormSelect,
    FormTimeInput,
    Card,
    Alert,
    TradingStrategySelect,
    SegmentedControl,
    Badge,
    ExitCandidateSelect
} from '@/components/UI';
import EntitySelectionPanel, { type EntityConfig } from '@/components/UI/EntitySelectionPanel';
import ScreenerUniverseSelector, { type ScreenerUniverseEntityType } from '@/components/Screeners/ScreenerUniverseSelector';
import AutoDiscoveryConfig from '@/components/Experiments/AutoDiscoveryConfig';
import { OptionStrategyOverrideEditor, type OptionStrategyOverrides } from '@/components/Experiments/OptionStrategyOverrideEditor';
import type { AppRouter } from '@/types/appRouter';
import dayjs from 'dayjs';
import { getResearchWindow, STANDARD_DURATIONS } from '@einhaus/project314-shared';
import type { OptionBuyingStrategy, TradingStrategy } from '@einhaus/project314-types';
import { useOptionBuyingStrategies } from '@/hooks/useOptionBuyingStrategies';
import { formatOptionBuyingStrategyMeta, formatOptionBuyingStrategyName } from '@/utils/optionBuyingStrategyHelpers';
import {
    parseStoredAutoDiscoveryConfig,
    parseStoredTimeHorizons,
    serializeExperimentHorizonConfig,
    type AutoDiscoveryConfigValue
} from '@/utils/experimentHorizonConfig';

// Type definitions
type DurationType = (typeof STANDARD_DURATIONS)[number];
type ExperimentUniverseMode = 'entities' | 'screener';
type CreateExperimentInput = inferRouterInputs<AppRouter>['experiment']['create'];
type ObjectiveMode = 'fixed_horizon' | 'strategy_exit';

// Duration options
const DURATION_OPTIONS: { value: DurationType; label: string }[] = STANDARD_DURATIONS.map((d) => ({ value: d, label: d }));

const UNIVERSE_MODE_OPTIONS: { value: ExperimentUniverseMode; label: string }[] = [
    { value: 'entities', label: 'Entity list' },
    { value: 'screener', label: 'Screener' }
];

const uniqueSorted = (values: readonly number[]): number[] => [...new Set(values)].sort((a, b) => a - b);

const effectiveOptionBuyingStrategyIdsForLeg = (strategy: TradingStrategy, leg: TradingStrategy['legs'][number]): number[] => {
    const legOverrideIds = leg.autoBacktestOptionBuyingStrategiesOverride ?? [];

    return legOverrideIds.length > 0 ? legOverrideIds : (strategy.autoBacktestOptionBuyingStrategiesOverride ?? []);
};

const summarizeStrategyOptionTargets = (strategy: TradingStrategy | null) => {
    if (!strategy) {
        return {
            allowedOptionBuyingStrategyIds: [] as number[],
            ambiguous: false,
            hasOptionLegs: false,
            optionLegCount: 0
        };
    }

    const optionLegs = strategy.legs.filter((leg) => leg.derivativeType === 'Option');
    const targetSets = optionLegs.map((leg) => effectiveOptionBuyingStrategyIdsForLeg(strategy, leg));

    return {
        allowedOptionBuyingStrategyIds: uniqueSorted(targetSets.flat()),
        ambiguous: optionLegs.length !== 1 || targetSets.some((ids) => ids.length !== 1),
        hasOptionLegs: optionLegs.length > 0,
        optionLegCount: optionLegs.length
    };
};

const hasDurationOverrideCoverage = (overrides: OptionStrategyOverrides | null, durations: readonly string[]): boolean => {
    if (overrides?.default !== undefined) return true;

    return durations.every((duration) => overrides?.byDuration?.[duration] !== undefined);
};

const parseStoredOptionStrategyOverrides = (rawOverrides: string | null | undefined): OptionStrategyOverrides | null => {
    if (!rawOverrides) return null;

    try {
        const parsed = JSON.parse(rawOverrides) as OptionStrategyOverrides;

        return Object.keys(parsed).length > 0 ? parsed : null;
    } catch {
        return null;
    }
};

const parseStoredOptionStrategyConfig = (rawConfig: string | null | undefined): Record<string, number> | null => {
    if (!rawConfig) return null;

    try {
        const parsed = JSON.parse(rawConfig) as Record<string, number>;
        return Object.keys(parsed).length > 0 ? parsed : null;
    } catch {
        return null;
    }
};

const formatOptionStrategyLabel = (strategy: OptionBuyingStrategy | undefined, strategyId: number): string => {
    if (!strategy) {
        return `Option strategy #${strategyId}`;
    }

    return `${formatOptionBuyingStrategyName(strategy)} (#${strategyId}) - ${formatOptionBuyingStrategyMeta(strategy)}`;
};

// Parse a positive-integer form value (trimmed). Returns null for blank/non-numeric/≤0 input.
const parsePositiveIntInput = (raw: string): number | null => {
    const parsed = Number.parseInt(raw.trim(), 10);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

// Validate the strategy_exit objective inputs. Returns a partial errors map (empty when valid or not applicable).
const objectiveValidationErrors = (objectiveMode: ObjectiveMode, exitCandidateIdRaw: string): Record<string, string> => {
    if (objectiveMode !== 'strategy_exit') return {};

    if (parsePositiveIntInput(exitCandidateIdRaw) === null) {
        return {
            objectiveExitCandidateId: 'Select an active global exit candidate.'
        };
    }

    return {};
};

// Build the experiment `objective` input from form state. Falls back to fixed_horizon unless a strategy_exit
// mode has a valid exit candidate id (validateForm guarantees this before submit). When no option buying
// strategy is resolved yet, the legs are omitted and the backend derives the same best-ranked AERO pairing.
const buildObjectiveInput = (
    objectiveMode: ObjectiveMode,
    exitCandidateIdRaw: string,
    optionBuyingStrategyId: number | null
): CreateExperimentInput['objective'] => {
    const exitCandidateId = objectiveMode === 'strategy_exit' ? parsePositiveIntInput(exitCandidateIdRaw) : null;

    if (exitCandidateId === null) {
        return { mode: 'fixed_horizon' };
    }

    return {
        mode: 'strategy_exit',
        exitCandidateId,
        ...(optionBuyingStrategyId !== null ? { optionBuyingStrategyIds: [optionBuyingStrategyId] } : {})
    };
};

const ExperimentEditor: React.FC = () => {
    const [defaultResearchWindow] = useState(() => getResearchWindow(OPTIONS_DATA_START_DATETIME, new Date()));
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { id } = useParams<{ id?: string }>();
    const isEditMode = !!id;
    const experimentId = Number.parseInt(id ?? '0', 10);
    const [hydratedExperimentId, setHydratedExperimentId] = useState<number | null>(null);
    const [hydrationFailure, setHydrationFailure] = useState<{ experimentId: number; message: string } | null>(null);

    // Form state
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [strategyId, setStrategyId] = useState<number | null>(null);
    const [universeMode, setUniverseMode] = useState<ExperimentUniverseMode>('entities');
    const [selectedEntities, setSelectedEntities] = useState<EntityConfig[]>([]);
    // Entity list the universe was resolved from, when one was used. Persisted alongside the
    // entities so editing and copying an experiment show the list rather than its expansion.
    const [entityListId, setEntityListId] = useState<number | null>(null);
    const [screenerRuleId, setScreenerRuleId] = useState<number | null>(null);
    const [screenerEntityTypes, setScreenerEntityTypes] = useState<ScreenerUniverseEntityType[]>([]);
    const [selectedDurations, setSelectedDurations] = useState<DurationType[]>(['1d']);
    // Parse OPTIONS_DATA_START_DATETIME to get default date and time
    const defaultStartDateTime = dayjs(OPTIONS_DATA_START_DATETIME);
    const [startDate, setStartDate] = useState(defaultStartDateTime.format('YYYY-MM-DD'));
    const [startTime, setStartTime] = useState(defaultStartDateTime.format('HH:mm:ss'));
    const defaultEndDateTime = dayjs(defaultResearchWindow.walkForwardEnd);
    const [endDate, setEndDate] = useState(defaultEndDateTime.format('YYYY-MM-DD'));
    const [endTime, setEndTime] = useState(defaultEndDateTime.format('HH:mm:ss'));

    // Options configuration
    const [includeOptions, setIncludeOptions] = useState(false);
    const [optionStrategyConfig, setOptionStrategyConfig] = useState<Record<string, number> | null>(null);
    const [optionStrategyOverrides, setOptionStrategyOverrides] = useState<OptionStrategyOverrides | null>(null);
    const [tradeLogicV2EntryVersionIdOverride, setTradeLogicV2EntryVersionIdOverride] = useState<number | null>(null);
    const [purpose, setPurpose] = useState<CreateExperimentInput['purpose']>('standard');

    // Auto-discovery configuration
    const [autoDiscoveryConfig, setAutoDiscoveryConfig] = useState<AutoDiscoveryConfigValue | null>(null);
    const [storedTimeHorizons, setStoredTimeHorizons] = useState<number[] | null>(null);

    // Detailed logging toggle
    const [enableDetailedLogging, setEnableDetailedLogging] = useState(false);

    // Objective: fixed_horizon (default) or a strategy_exit global exit candidate that downstream discovery,
    // scorecards, and the screener optimizer score against. The realized outcome set is materialized
    // automatically after the experiment completes — there is no manual materialize step here.
    const [objectiveMode, setObjectiveMode] = useState<ObjectiveMode>('fixed_horizon');
    const [objectiveExitCandidateId, setObjectiveExitCandidateId] = useState('');
    // Option buying strategy the realized objective replays with. Follows the exit candidate's best-ranked
    // AERO pairing until the user overrides (or an edit-mode seed pins the stored objective legs).
    const [objectiveOptionBuyingStrategyId, setObjectiveOptionBuyingStrategyId] = useState<number | null>(null);
    const [objectiveObsUserOverridden, setObjectiveObsUserOverridden] = useState(false);

    const [error, setError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    const strategyIdFromQuery = useMemo(() => {
        const v = Number(searchParams.get('strategyId'));
        return !Number.isNaN(v) && v > 0 ? v : null;
    }, [searchParams]);

    const backToExperimentsUrl = useMemo(() => {
        return strategyIdFromQuery ? `/user/experiments?strategyId=${strategyIdFromQuery}` : '/user/experiments';
    }, [strategyIdFromQuery]);

    // Strategies are fetched via the shared hook; the TradingStrategySelect below
    // uses the same hook, so React Query shares a single request between them.
    const { error: strategiesError, isLoading: isLoadingStrategies, strategies } = useTradingStrategies();
    const { strategiesById: optionBuyingStrategiesById } = useOptionBuyingStrategies();

    // Names the universe by its entity list in the summary. Shares the entity-list query with the
    // selection panel below, so this adds no request.
    const { entityLists } = useEntityLists({ enabled: entityListId !== null });
    const universeEntityListName = useMemo(
        () => (entityListId === null ? null : (entityLists.find((list) => list.id === entityListId)?.name ?? null)),
        [entityLists, entityListId]
    );

    const selectedStrategy = useMemo(() => strategies.find((strategy) => strategy.id === strategyId) ?? null, [strategies, strategyId]);

    const selectedStrategyOptionTargets = useMemo(() => summarizeStrategyOptionTargets(selectedStrategy), [selectedStrategy]);

    const optionStrategyDefaultValue = optionStrategyOverrides?.default ?? null;

    const optionStrategyDefaultIsAllowed =
        optionStrategyDefaultValue !== null &&
        selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds.includes(optionStrategyDefaultValue);

    const effectiveOptionStrategyOverrides = useMemo(() => {
        if (!optionStrategyOverrides) {
            return null;
        }

        if (optionStrategyOverrides.default === undefined || optionStrategyDefaultIsAllowed) {
            return optionStrategyOverrides;
        }

        const remainingOverrides = { ...optionStrategyOverrides };
        delete remainingOverrides.default;

        return Object.keys(remainingOverrides).length > 0 ? remainingOverrides : null;
    }, [optionStrategyDefaultIsAllowed, optionStrategyOverrides]);

    const optionStrategyTargetOptions = useMemo(
        () => [
            { label: 'Select option buying strategy...', value: '' },
            ...selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds.map((optionStrategyId) => ({
                label: formatOptionStrategyLabel(optionBuyingStrategiesById.get(optionStrategyId), optionStrategyId),
                value: optionStrategyId
            }))
        ],
        [optionBuyingStrategiesById, selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds]
    );

    const setDefaultOptionStrategyOverride = (optionStrategyId: number | null) => {
        setOptionStrategyOverrides((previous) => {
            const next: OptionStrategyOverrides = { ...(previous ?? {}) };

            if (optionStrategyId === null) {
                delete next.default;
            } else {
                next.default = optionStrategyId;
            }

            return Object.keys(next).length > 0 ? next : null;
        });
    };

    // If we were created from a filtered experiments list, pre-select that strategy.
    useEffect(() => {
        if (isEditMode) return;
        if (strategyId) return;

        if (strategyIdFromQuery) {
            setStrategyId(strategyIdFromQuery);
        }
    }, [isEditMode, strategyId, strategyIdFromQuery]);

    // Fetch existing experiment if in edit mode
    const experimentQuery = useAppQuery(
        {
            ...trpc.experiment.status.queryOptions({ experimentId }),
            enabled: isEditMode,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load experiment',
            setError: (msg) => setError(msg)
        }
    );

    // Fetch the experiment's objective(s) in edit mode so the editor reflects the current primary objective.
    const objectivesQuery = useAppQuery(
        {
            ...trpc.experiment.listObjectives.queryOptions({ experimentId }),
            enabled: isEditMode,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load experiment objective'
        }
    );

    const primaryObjective = objectivesQuery.data?.find((objective) => objective.isPrimary === 1) ?? null;

    // Create experiment mutation
    const createMutation = useTrpcMutation(
        trpc.experiment.create.mutationOptions({
            onSuccess: () => {
                // Invalidate the experiments list query to refresh the data
                void queryClient.invalidateQueries({
                    queryKey: [['experiment', 'list']]
                });

                void navigate(backToExperimentsUrl);
            },
            onError: (error) => {
                setError(`Failed to create experiment: ${error.message}`);
            }
        }),
        {
            successMessage: 'Experiment created successfully',
            fallbackErrorMessage: 'Failed to create experiment'
        }
    );

    // Update experiment mutation
    const updateMutation = useTrpcMutation(
        trpc.experiment.update.mutationOptions({
            onSuccess: () => {
                // Invalidate the experiments list query to refresh the data
                void queryClient.invalidateQueries({
                    queryKey: [['experiment', 'list']]
                });

                void navigate(backToExperimentsUrl);
            },
            onError: (error) => {
                setError(`Failed to update experiment: ${error.message}`);
            }
        }),
        {
            successMessage: 'Experiment updated successfully',
            fallbackErrorMessage: 'Failed to update experiment'
        }
    );

    // Populate form when editing
    useEffect(() => {
        if (hydratedExperimentId === experimentId) return;

        if (isEditMode && experimentQuery.data?.experiment?.id === experimentId && objectivesQuery.data) {
            const exp = experimentQuery.data.experiment;

            // Parse the JSON fields
            try {
                const entities = exp.entities;
                const durations = JSON.parse(exp.durations || '[]') as DurationType[];

                // Set form fields
                setName(exp.name);
                setDescription(exp.description || '');
                setStrategyId(exp.strategyId);
                setUniverseMode('entities');
                setSelectedEntities(entities);
                // Provenance, not a re-resolution: the stored snapshot stays as-is, the id only lets
                // the panel show the originating list instead of every entity it expanded into.
                // `?? null` covers gateway deploy skew: an older compiled artifact omits the field
                // entirely, and undefined would read as "a list is selected" downstream.
                setEntityListId(exp.entityListId ?? null);
                setScreenerRuleId(null);
                setScreenerEntityTypes([]);
                setSelectedDurations(durations);

                // Parse dates and times
                const startDT = dayjs(exp.startDateTime);
                const endDT = dayjs(exp.endDateTime);

                setStartDate(startDT.format('YYYY-MM-DD'));
                setStartTime(startDT.format('HH:mm:ss'));
                setEndDate(endDT.format('YYYY-MM-DD'));
                setEndTime(endDT.format('HH:mm:ss'));
                setIncludeOptions(Boolean(exp.includeOptions));
                setOptionStrategyConfig(parseStoredOptionStrategyConfig(exp.optionStrategyConfig));
                setOptionStrategyOverrides(parseStoredOptionStrategyOverrides(exp.optionStrategyOverrides));
                setAutoDiscoveryConfig(parseStoredAutoDiscoveryConfig(exp.customHorizons, durations, exp.autoParameterDiscovery));
                setStoredTimeHorizons(parseStoredTimeHorizons(exp.customHorizons));
                setEnableDetailedLogging(exp.enableDetailedLogging);
                setTradeLogicV2EntryVersionIdOverride(exp.tradeLogicV2EntryVersionIdOverride);
                setPurpose(exp.purpose);

                const storedPrimaryObjective = objectivesQuery.data.find((objective) => objective.isPrimary === 1) ?? null;

                if (storedPrimaryObjective) {
                    setObjectiveMode(storedPrimaryObjective.mode);
                    setObjectiveExitCandidateId(
                        storedPrimaryObjective.exitCandidateId !== null ? String(storedPrimaryObjective.exitCandidateId) : ''
                    );
                    // Pin the stored objective legs so a resubmit never silently re-derives a different pairing.
                    setObjectiveOptionBuyingStrategyId(storedPrimaryObjective.optionBuyingStrategyIdLeg1 ?? null);
                    setObjectiveObsUserOverridden(storedPrimaryObjective.optionBuyingStrategyIdLeg1 !== null);
                } else {
                    setObjectiveMode('fixed_horizon');
                    setObjectiveExitCandidateId('');
                    setObjectiveOptionBuyingStrategyId(null);
                    setObjectiveObsUserOverridden(false);
                }

                // Publish hydration last so the editable form cannot submit its default state for a copied experiment.
                setHydrationFailure(null);
                setHydratedExperimentId(exp.id);
            } catch (err) {
                console.error('Error parsing experiment data:', err);
                setHydrationFailure({ experimentId: exp.id, message: 'Failed to load experiment data' });
            }
        }
    }, [experimentId, hydratedExperimentId, isEditMode, experimentQuery.data, objectivesQuery.data]);

    // Handle duration toggle
    const handleDurationToggle = (duration: DurationType) => {
        setSelectedDurations((prev) => {
            if (prev.includes(duration)) {
                return prev.filter((d) => d !== duration);
            }

            return [...prev, duration];
        });
    };

    // The selected global candidate drives realized objective validation, pairing resolution, and submission.
    const selectedExitCandidateId = parsePositiveIntInput(objectiveExitCandidateId);

    // The option-buying-strategy pairings this candidate was simulated with (best AERO rank first). An AERO
    // grid pairs the same exit rules with several option strategies, so the candidate alone does not
    // determine the option — the best-ranked pairing is the one that earned the candidate its rank.
    const objectivePairingsQuery = useAppQuery(
        {
            ...trpc.experiment.probeExitCandidateOptionPairings.queryOptions({ exitCandidateId: selectedExitCandidateId ?? 0 }),
            enabled: objectiveMode === 'strategy_exit' && selectedExitCandidateId !== null,
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load AERO option pairings for the exit candidate'
        }
    );

    const objectiveObsPairings = useMemo(
        () => (selectedExitCandidateId !== null ? (objectivePairingsQuery.data?.pairings ?? []) : []),
        [objectivePairingsQuery.data, selectedExitCandidateId]
    );

    const compatibleObjectiveObsPairings = useMemo(() => {
        const allowedIds = new Set(selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds);
        return objectiveObsPairings.filter((pairing) => allowedIds.has(pairing.optionBuyingStrategyId));
    }, [objectiveObsPairings, selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds]);

    const bestObjectiveObsPairing = compatibleObjectiveObsPairings[0] ?? null;
    const autoObjectiveOptionBuyingStrategyId =
        bestObjectiveObsPairing?.optionBuyingStrategyId ??
        (selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds.length === 1
            ? selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds[0]!
            : null);

    // The user's explicit choice wins; otherwise follow the best-ranked pairing that the target strategy
    // actually permits, falling back to its sole unambiguous configured option target.
    const effectiveObjectiveOptionBuyingStrategyId = objectiveObsUserOverridden
        ? objectiveOptionBuyingStrategyId
        : autoObjectiveOptionBuyingStrategyId;

    const objectiveObsSelectOptions = useMemo(() => {
        const pairingByObs = new Map(compatibleObjectiveObsPairings.map((pairing) => [pairing.optionBuyingStrategyId, pairing]));
        const optionStrategyIds = uniqueSorted(selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds);

        return [
            { label: 'Auto — best compatible AERO pairing or strategy default', value: '' },
            ...optionStrategyIds.map((optionStrategyId) => {
                const pairing = pairingByObs.get(optionStrategyId);
                const rankSuffix = pairing?.rankOverall != null ? ` · AERO rank ${pairing.rankOverall}` : '';

                return {
                    label: `${formatOptionStrategyLabel(optionBuyingStrategiesById.get(optionStrategyId), optionStrategyId)}${rankSuffix}`,
                    value: optionStrategyId
                };
            })
        ];
    }, [compatibleObjectiveObsPairings, optionBuyingStrategiesById, selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds]);

    // Coherence: options performance tracking should mark the same option the realized objective replays
    // with, unless the user already chose a tracking default explicitly.
    const prefillTrackingDefaultFromObjective = (optionStrategyId: number | null) => {
        if (optionStrategyId === null || !includeOptions) return;
        if (optionStrategyOverrides?.default !== undefined) return;

        setDefaultOptionStrategyOverride(optionStrategyId);
    };

    const handleObjectiveObsChange = (rawValue: string) => {
        const parsedValue = Number(rawValue);
        const optionStrategyId = Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;

        setObjectiveOptionBuyingStrategyId(optionStrategyId);
        setObjectiveObsUserOverridden(optionStrategyId !== null);
        prefillTrackingDefaultFromObjective(optionStrategyId);
    };

    const handleIncludeOptionsChange = (checked: boolean) => {
        setIncludeOptions(checked);

        if (checked && optionStrategyOverrides?.default === undefined && effectiveObjectiveOptionBuyingStrategyId !== null) {
            setDefaultOptionStrategyOverride(effectiveObjectiveOptionBuyingStrategyId);
        }
    };

    // A changed candidate invalidates any option-strategy override (it belonged to the old pairing).
    const resetObjectiveObsSelection = () => {
        setObjectiveOptionBuyingStrategyId(null);
        setObjectiveObsUserOverridden(false);
    };

    const clearObjectiveExitCandidateError = () => {
        setValidationErrors((prev) => {
            if (!prev.objectiveExitCandidateId) return prev;

            const next = { ...prev };
            delete next.objectiveExitCandidateId;

            return next;
        });
    };

    const handleStrategyChange = (nextStrategyId: number | null) => {
        if (nextStrategyId === strategyId) return;

        setStrategyId(nextStrategyId);
        // Exit candidates are global and remain reusable; only the strategy-specific option pairing becomes stale.
        resetObjectiveObsSelection();
    };

    const handleExitCandidateChange = (candidateId: number | null) => {
        setObjectiveExitCandidateId(candidateId === null ? '' : String(candidateId));
        resetObjectiveObsSelection();
        clearObjectiveExitCandidateError();
    };

    // Validate form
    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!name.trim()) {
            errors.name = 'Name is required';
        }

        if (!strategyId || strategyId === 0) {
            errors.strategy = 'Strategy is required';
        }

        if (universeMode === 'entities' && selectedEntities.length === 0) {
            errors.entities = 'At least one entity is required';
        }

        if (universeMode === 'screener' && screenerRuleId === null) {
            errors.screenerRuleId = 'Screener rule is required';
        }

        if (selectedDurations.length === 0) {
            errors.durations = 'At least one duration is required';
        }

        if (selectedStrategyOptionTargets.hasOptionLegs && !includeOptions) {
            errors.includeOptions = 'This strategy has option legs, so options tracking is required.';
        }

        if (
            includeOptions &&
            selectedStrategyOptionTargets.ambiguous &&
            !hasDurationOverrideCoverage(effectiveOptionStrategyOverrides, selectedDurations)
        ) {
            errors.optionStrategyDefault = 'Select a default option buying strategy or add duration overrides for every selected duration.';
        }

        const startDateTime = dayjs(`${startDate} ${startTime}`);
        const endDateTime = dayjs(`${endDate} ${endTime}`);

        if (endDateTime.isBefore(startDateTime)) {
            errors.dateRange = 'End date must be after start date';
        }

        Object.assign(errors, objectiveValidationErrors(objectiveMode, objectiveExitCandidateId));

        if (
            objectiveMode === 'strategy_exit' &&
            selectedStrategyOptionTargets.hasOptionLegs &&
            effectiveObjectiveOptionBuyingStrategyId === null
        ) {
            errors.objectiveOptionBuyingStrategyId = 'Select an option buying strategy allowed by the target strategy for realized replay.';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }

        const startDateTime = `${startDate} ${startTime}`;
        const endDateTime = `${endDate} ${endTime}`;

        const objective = buildObjectiveInput(objectiveMode, objectiveExitCandidateId, effectiveObjectiveOptionBuyingStrategyId);
        const customHorizons = serializeExperimentHorizonConfig(storedTimeHorizons, autoDiscoveryConfig);

        const mutationData: CreateExperimentInput = {
            name,
            description: description || undefined,
            strategyId: strategyId!, // We know it's not null after validation
            durations: selectedDurations,
            startDateTime,
            endDateTime,
            includeOptions,
            optionStrategyConfig,
            optionStrategyOverrides: effectiveOptionStrategyOverrides,
            autoParameterDiscovery: !!autoDiscoveryConfig,
            // Preserve any fixed-horizon override while storing the auto-discovery selection for Rust workers.
            customHorizons,
            enableDetailedLogging,
            tradeLogicV2EntryVersionIdOverride,
            purpose,
            objective
        };

        if (universeMode === 'screener') {
            mutationData.universe = {
                type: 'screenerRuleId',
                screenerRuleId: screenerRuleId!,
                ...(screenerEntityTypes.length > 0 ? { entityTypes: screenerEntityTypes } : {})
            };
        } else if (entityListId !== null) {
            // Pin the exact entities while recording the list they came from. Sending the bare list id
            // would re-expand it to its current members, which can differ from what is on screen.
            mutationData.universe = {
                type: 'resolvedFromEntityList',
                sourceEntityListId: entityListId,
                entities: selectedEntities
            };
        } else {
            mutationData.entities = selectedEntities;
        }

        if (isEditMode) {
            // Update existing experiment
            updateMutation.mutate({
                experimentId,
                ...mutationData
            });
        } else {
            // Create new experiment
            createMutation.mutate(mutationData);
        }
    };

    // Calculate total combinations
    const totalCombinations = useMemo(() => {
        if (universeMode === 'screener') return null;

        return selectedEntities.length * selectedDurations.length;
    }, [selectedEntities, selectedDurations, universeMode]);

    const currentHydrationFailure = hydrationFailure?.experimentId === experimentId ? hydrationFailure.message : null;
    const editLoadError = isEditMode
        ? (experimentQuery.error?.message ??
          objectivesQuery.error?.message ??
          (experimentQuery.isSuccess && !experimentQuery.data.experiment ? 'Experiment not found' : null) ??
          currentHydrationFailure)
        : null;

    // Keep the edit form unavailable until every persisted field, including the objective and horizon
    // configuration, has been loaded into state. A load failure gets a dedicated non-editable error view.
    const isLoading =
        isLoadingStrategies ||
        (isEditMode && !editLoadError && (experimentQuery.isLoading || objectivesQuery.isLoading || hydratedExperimentId !== experimentId));

    // Handle strategy load errors (useTradingStrategies does not support errorConfig,
    // so the banner derives from strategiesLoadError and the toast fires in an effect)
    const strategiesLoadError = strategiesError ? strategiesError.message || 'Failed to load strategies' : null;

    useEffect(() => {
        if (strategiesLoadError) {
            showToast(strategiesLoadError, 'error');
        }
    }, [strategiesLoadError]);

    const displayedError = error ?? strategiesLoadError;
    const executionFailure = experimentQuery.data?.experiment?.executionStatus === 'failed' ? experimentQuery.data.experiment.error : null;

    useEffect(() => {
        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        return () => hideSpinner();
    }, [isLoading]);

    if (isLoading) {
        return (
            <UserLayout title="Experiment Editor - Project 314">
                <DataLoadingSpinner text="Loading..." />
            </UserLayout>
        );
    }

    if (editLoadError) {
        return (
            <UserLayout title="Experiment Editor - Project 314">
                <div className="container mx-auto px-4">
                    <Alert className="mb-4" variant="danger">
                        {editLoadError}
                    </Alert>
                    <Button variant="secondary" onClick={() => void navigate(backToExperimentsUrl)}>
                        Back to Experiments
                    </Button>
                </div>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1200px" title={`${isEditMode ? 'Edit' : 'Create'} Experiment - Project 314`}>
            <div className="container mx-auto px-4">
                <div className="mb-6">
                    <h3 className="text-white text-xl font-semibold mb-2">{isEditMode ? 'Edit' : 'Create New'} Experiment</h3>
                    <p className="text-zinc-400 text-sm">
                        Configure an experiment to test your strategy across multiple entities and timeframes. Time horizons are
                        automatically generated based on the selected durations.
                    </p>
                </div>

                {displayedError && (
                    <Alert className="mb-4" variant="danger">
                        {displayedError}
                    </Alert>
                )}

                {executionFailure && (
                    <Alert className="mb-4" variant="danger">
                        <p className="font-semibold">Experiment execution failed</p>
                        <p className="mt-1 break-words text-sm">{executionFailure}</p>
                    </Alert>
                )}

                <div className="space-y-6">
                    {/* Basic Information */}
                    <Card>
                        <h4 className="text-white font-semibold mb-4">Basic Information</h4>
                        <div className="space-y-4">
                            <FormInput
                                required
                                error={validationErrors.name}
                                label="Experiment Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            <FormInput
                                label="Description"
                                placeholder="Optional description of the experiment"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                            <TradingStrategySelect
                                error={validationErrors.strategy}
                                label="Trading Strategy"
                                value={strategyId}
                                onChange={handleStrategyChange}
                            />
                        </div>
                    </Card>

                    {/* Scoring Objective */}
                    <Card>
                        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <h4 className="text-white font-semibold">Scoring Objective</h4>
                            <div className="flex items-center gap-2">
                                <SegmentedControl<ObjectiveMode>
                                    options={[
                                        { value: 'fixed_horizon', label: 'Fixed Horizon' },
                                        { value: 'strategy_exit', label: 'Realized (global exit)' }
                                    ]}
                                    value={objectiveMode}
                                    onChange={setObjectiveMode}
                                />
                                {isEditMode && primaryObjective?.mode === 'strategy_exit' && (
                                    <Badge
                                        variant={
                                            primaryObjective.status === 'ready'
                                                ? 'success'
                                                : primaryObjective.status === 'failed'
                                                  ? 'danger'
                                                  : 'warning'
                                        }
                                    >
                                        {primaryObjective.status === 'ready'
                                            ? 'Realized data ready'
                                            : primaryObjective.status === 'failed'
                                              ? 'Preparation failed'
                                              : 'Preparing realized data'}
                                    </Badge>
                                )}
                            </div>
                        </div>

                        {objectiveMode === 'fixed_horizon' ? (
                            <p className="text-sm text-zinc-400">
                                Fixed-horizon marks each entry at a fixed N-day horizon — an exploratory entry-edge proxy. Switch to
                                Realized to score parameter discovery, scorecards, and the screener optimizer against a specific published
                                global exit candidate.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                <ExitCandidateSelect
                                    collapseEquivalent
                                    error={validationErrors.objectiveExitCandidateId}
                                    helpText="Search every active global candidate. Candidates with identical exit behavior are grouped; this strategy's attached candidate is shown when one exists. Search by label, ID, hash, fingerprint, source, or description."
                                    isDisabled={!strategyId}
                                    label="Global exit candidate"
                                    mode="single"
                                    placeholder={strategyId ? 'Search global exit candidates…' : 'Select the target strategy first'}
                                    preferredStrategyId={strategyId ?? undefined}
                                    value={selectedExitCandidateId}
                                    onChange={handleExitCandidateChange}
                                />

                                {!strategyId && (
                                    <div className="text-xs text-amber-300">
                                        Select the experiment&apos;s target strategy before choosing a global exit candidate.
                                    </div>
                                )}

                                {selectedExitCandidateId !== null && selectedStrategyOptionTargets.hasOptionLegs && (
                                    <div>
                                        <FormSelect
                                            error={validationErrors.objectiveOptionBuyingStrategyId}
                                            label="Option Buying Strategy (realized replay)"
                                            options={objectiveObsSelectOptions}
                                            value={effectiveObjectiveOptionBuyingStrategyId ?? ''}
                                            onChange={(event) => handleObjectiveObsChange(event.target.value)}
                                        />
                                        {!objectiveObsUserOverridden && bestObjectiveObsPairing !== null && (
                                            <p className="mt-1 text-xs text-zinc-500">
                                                Auto-selected from AERO job {bestObjectiveObsPairing.jobId}
                                                {bestObjectiveObsPairing.rankOverall !== null
                                                    ? ` (rank ${bestObjectiveObsPairing.rankOverall})`
                                                    : ''}{' '}
                                                — the pairing that earned this candidate its rank. The realized outcome set replays entries
                                                with this option strategy.
                                            </p>
                                        )}
                                        {!objectivePairingsQuery.isFetching &&
                                            compatibleObjectiveObsPairings.length === 0 &&
                                            effectiveObjectiveOptionBuyingStrategyId === null && (
                                                <p className="mt-1 text-xs text-amber-300">
                                                    No AERO pairing found for this candidate — select the option strategy the realized
                                                    replay should use
                                                    {selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds.length > 1
                                                        ? ' (this strategy has multiple configured option strategies)'
                                                        : ''}
                                                    .
                                                </p>
                                            )}
                                    </div>
                                )}

                                <Alert variant="info">
                                    The realized outcome set for this exit is materialized automatically once the experiment completes —
                                    there is no manual materialize step. Parameter discovery, scorecards, and the screener optimizer will
                                    default to this objective.
                                </Alert>
                            </div>
                        )}
                    </Card>

                    {/* Universe Selection */}
                    <Card>
                        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <h4 className="text-white font-semibold">Entry Universe</h4>
                            <div className="inline-flex w-fit rounded-md border border-zinc-700 bg-black/30 p-0.5">
                                {UNIVERSE_MODE_OPTIONS.map((option) => (
                                    <button
                                        className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                                            universeMode === option.value ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:text-zinc-200'
                                        }`}
                                        key={option.value}
                                        type="button"
                                        onClick={() => setUniverseMode(option.value)}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {universeMode === 'entities' ? (
                            <EntitySelectionPanel
                                entityListId={entityListId}
                                error={validationErrors.entities}
                                selectedEntities={selectedEntities}
                                onEntityListIdChange={setEntityListId}
                                onEntitiesChange={setSelectedEntities}
                            />
                        ) : (
                            <ScreenerUniverseSelector
                                entityTypes={screenerEntityTypes}
                                error={validationErrors.screenerRuleId}
                                screenerRuleId={screenerRuleId}
                                onEntityTypesChange={setScreenerEntityTypes}
                                onScreenerRuleIdChange={setScreenerRuleId}
                            />
                        )}
                    </Card>

                    {/* Duration Selection */}
                    <Card>
                        <h4 className="text-white font-semibold mb-4">Select Durations</h4>
                        {validationErrors.durations && <p className="text-red-400 text-sm mb-2">{validationErrors.durations}</p>}
                        <div className="grid grid-cols-4 gap-3">
                            {DURATION_OPTIONS.map((duration) => (
                                <label className="flex items-center space-x-2 cursor-pointer" key={duration.value}>
                                    <input
                                        checked={selectedDurations.includes(duration.value)}
                                        className="rounded border-zinc-700 bg-zinc-900 text-blue-500 focus:ring-blue-500"
                                        type="checkbox"
                                        onChange={() => handleDurationToggle(duration.value)}
                                    />
                                    <span className="text-zinc-300 text-sm">{duration.label}</span>
                                </label>
                            ))}
                        </div>
                    </Card>

                    {/* Options Configuration */}
                    <Card>
                        <h4 className="text-white font-semibold mb-4">Options Analysis</h4>
                        <div className="space-y-4">
                            <div className="flex items-center space-x-3 cursor-pointer">
                                <input
                                    checked={includeOptions}
                                    className="rounded border-zinc-700 bg-zinc-900 text-blue-500 focus:ring-blue-500"
                                    id="include-options"
                                    type="checkbox"
                                    onChange={(e) => handleIncludeOptionsChange(e.target.checked)}
                                />
                                <div>
                                    <span className="text-zinc-300">Include Options Performance Tracking</span>
                                    <p className="text-zinc-500 text-xs mt-1">
                                        Track option profitability alongside stock performance using default option strategies per duration
                                    </p>
                                </div>
                            </div>
                            {validationErrors.includeOptions && <p className="text-red-400 text-sm">{validationErrors.includeOptions}</p>}

                            {includeOptions && (
                                <>
                                    {selectedStrategyOptionTargets.ambiguous && (
                                        <div className="rounded border border-amber-800 bg-amber-950/20 p-3">
                                            <p className="mb-3 text-sm text-amber-100">
                                                This strategy has {selectedStrategyOptionTargets.optionLegCount} option leg
                                                {selectedStrategyOptionTargets.optionLegCount === 1 ? '' : 's'} and{' '}
                                                {selectedStrategyOptionTargets.allowedOptionBuyingStrategyIds.length} configured option
                                                buying strategies. Select the experiment default explicitly.
                                            </p>
                                            <FormSelect
                                                noMargin
                                                error={validationErrors.optionStrategyDefault}
                                                label="Experiment Option Strategy"
                                                options={optionStrategyTargetOptions}
                                                value={optionStrategyDefaultIsAllowed ? optionStrategyDefaultValue : ''}
                                                onChange={(event) => {
                                                    const parsedValue = Number(event.target.value);

                                                    setDefaultOptionStrategyOverride(
                                                        Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null
                                                    );
                                                }}
                                            />
                                            {effectiveObjectiveOptionBuyingStrategyId !== null &&
                                                optionStrategyDefaultValue !== null &&
                                                optionStrategyDefaultValue !== effectiveObjectiveOptionBuyingStrategyId && (
                                                    <p className="mt-2 text-xs text-amber-300">
                                                        Options tracking uses option strategy #{optionStrategyDefaultValue} while the
                                                        scoring objective replays #{effectiveObjectiveOptionBuyingStrategyId} —
                                                        fixed-horizon option metrics and realized outcomes will describe different
                                                        instruments.
                                                    </p>
                                                )}
                                        </div>
                                    )}
                                    <div className="mt-4 p-3 bg-zinc-900 rounded border border-zinc-800">
                                        <p className="text-zinc-400 text-sm">
                                            Options will be selected based on each entry signal using the configured experiment option
                                            strategy:
                                        </p>
                                        <p className="mt-2 text-xs text-zinc-500">
                                            Assets and indexes use the selected option buying strategy's DTE, pricing, and spread rules.
                                            Index option outcomes must close before the contract's last trading time; expiration and cash
                                            settlement replay are not supported. An unavailable index random-entry baseline is shown as
                                            missing, never replaced with a stock baseline.
                                        </p>
                                    </div>

                                    <div className="mt-4">
                                        <OptionStrategyOverrideEditor
                                            disabled={createMutation.isPending}
                                            durations={selectedDurations}
                                            value={effectiveOptionStrategyOverrides}
                                            onChange={setOptionStrategyOverrides}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </Card>

                    {/* Date Range */}
                    <Card>
                        <h4 className="text-white font-semibold mb-4">Research Epoch</h4>
                        {validationErrors.dateRange && <p className="text-red-400 text-sm mb-2">{validationErrors.dateRange}</p>}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-zinc-400 text-sm mb-1" htmlFor="start-date">
                                    Start Date & Time
                                </label>
                                <div className="flex space-x-2">
                                    <FormDateInput
                                        noMargin
                                        className="flex-1"
                                        fullWidth={false}
                                        id="start-date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                    <FormTimeInput
                                        noMargin
                                        fullWidth={false}
                                        step="1"
                                        value={startTime}
                                        onChange={(e) => setStartTime(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="text-sm text-zinc-300 space-y-1">
                                {experimentQuery.data?.experiment?.trainingEndDateTime === null ||
                                experimentQuery.data?.experiment?.walkForwardEndDateTime === null ? (
                                    <p className="text-amber-400">Legacy experiment — rerun required for new research work.</p>
                                ) : (
                                    <>
                                        <p>
                                            Training ends:{' '}
                                            {experimentQuery.data?.experiment?.trainingEndDateTime ?? defaultResearchWindow.trainingEnd}
                                        </p>
                                        <p>
                                            Walk-forward ends:{' '}
                                            {experimentQuery.data?.experiment?.walkForwardEndDateTime ??
                                                defaultResearchWindow.walkForwardEnd}
                                        </p>
                                        <p className="text-zinc-500">
                                            Candidate selection uses training only; the committed candidate is evaluated on the full
                                            walk-forward.
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </Card>

                    {/* Auto Parameter Discovery */}
                    <AutoDiscoveryConfig
                        availableDurations={selectedDurations}
                        onChange={setAutoDiscoveryConfig}
                        value={autoDiscoveryConfig}
                    />

                    {/* Detailed Logging Toggle */}
                    <Card className="bg-zinc-950 border-zinc-800">
                        <h4 className="text-white font-semibold mb-4">Debug Options</h4>
                        <div className="flex items-center space-x-2">
                            <input
                                checked={enableDetailedLogging}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-zinc-600 rounded bg-zinc-800"
                                id="enableDetailedLogging"
                                type="checkbox"
                                onChange={(e) => setEnableDetailedLogging(e.target.checked)}
                            />
                            <label className="text-zinc-300 text-sm" htmlFor="enableDetailedLogging">
                                Enable detailed logging (creates large log files)
                            </label>
                        </div>
                        <p className="text-xs text-zinc-500 mt-2">
                            When enabled, detailed text logs will be created for debugging experiment runs and parameter discovery. This can
                            generate very large files (GB+) for long-running experiments.
                        </p>
                    </Card>

                    {/* Summary */}
                    <Card className="bg-blue-950/30 border-blue-800">
                        <h4 className="text-white font-semibold mb-4">Experiment Summary</h4>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                                <span className="text-zinc-400">Universe:</span>
                                <span className="text-white ml-2">
                                    {universeMode === 'screener'
                                        ? screenerRuleId === null
                                            ? 'No screener'
                                            : `Screener #${screenerRuleId}`
                                        : universeEntityListName
                                          ? `${universeEntityListName} (${selectedEntities.length} entities)`
                                          : `${selectedEntities.length} entities`}
                                </span>
                            </div>
                            <div>
                                <span className="text-zinc-400">Durations:</span>
                                <span className="text-white ml-2">{selectedDurations.length}</span>
                            </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-blue-800">
                            <div className="text-lg">
                                <span className="text-zinc-400">Total Combinations:</span>
                                <span className="text-white font-semibold ml-2">
                                    {totalCombinations === null ? 'Resolved at run time' : totalCombinations.toLocaleString()}
                                </span>
                            </div>
                            <p className="text-zinc-400 text-xs mt-2">
                                {universeMode === 'screener'
                                    ? 'The screener is evaluated at each entry timestamp before entry generation.'
                                    : 'Each combination will test the strategy for one entity and duration. Time horizons are automatically generated based on the duration.'}
                            </p>
                        </div>
                    </Card>

                    {/* Actions */}
                    <div className="flex justify-end space-x-4">
                        <Button variant="secondary" onClick={() => navigate(backToExperimentsUrl)}>
                            Cancel
                        </Button>
                        <Button disabled={createMutation.isPending || updateMutation.isPending} variant="primary" onClick={handleSubmit}>
                            {isEditMode
                                ? updateMutation.isPending
                                    ? 'Updating...'
                                    : 'Update Experiment'
                                : createMutation.isPending
                                  ? 'Creating...'
                                  : 'Create Experiment'}
                        </Button>
                    </div>
                </div>
            </div>
        </UserLayout>
    );
};

export default ExperimentEditor;
